import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
// Cash-register cue for AUTOMATION execution events only. The clip is
// intentionally short (~1 s ka-ching) so the burst-coalescing loop
// sounds continuous when fills stack — see TAIL_MS /
// MAX_EXECUTION_PLAY_MS below for the timing knobs that match the
// asset duration. Manual order fills get a different cue (see
// `manualSound`) so the operator can hear at a glance whether the
// fill they just heard came from automation or from their own click.
import cashRegisterSound from 'src/Sounds/cash-register.mp3';
// Distinct alert tone for errors / manual-intervention events.
// Re-uses the existing doorbell clip — was the previous full-fill
// sound, freed up when fills migrated to the cash register loop.
import alarmSound from 'src/Sounds/error.mp3';
// Achievement-style cue for MANUAL order fills (a single contract
// the operator placed via the Kalshi/Polymarket UI). One-shot, no
// loop — manual fills don't burst the way auto-unwind fills do.
import manualSound from 'src/Sounds/achive-sound.mp3';
// Single beep for TNA-negative crossings: the entry-side TNA just
// crossed below the profit line on a pair we hold — an OPPORTUNITY
// to act on, not an error. Lighter than the alarm doorbell (errors)
// and the double-beep (the prior "alert" framing), so the operator
// hears "heads up, look at this" without it sounding like a fault.
import tnaSound from 'src/Sounds/beep.mp3';
import type {
  NotificationEvent,
  NotificationEventType
} from '../types/notifications.types';

/**
 * Four distinct audio tiers for the notifications page.
 *
 *   1. **Alarm** — errors and manual-intervention events. Plays a
 *      distinct doorbell tone, throttled by ALARM_COOLDOWN_MS so a
 *      flurry of related errors doesn't blast the operator.
 *
 *   2. **Execution (automation)** — fills, partial fills, cycle
 *      complete from the auto-unwind engine. Plays a cash-register
 *      loop with burst coalescing (one continuous sound per burst,
 *      capped at MAX_EXECUTION_PLAY_MS).
 *
 *   3. **TNA** — a position's taker-side TNA just went negative
 *      (only fires when combined contracts ≥ 11; the producer
 *      gates that). Plays a distinct double-beep so the operator
 *      can tell "a real position went underwater" by ear, separately
 *      from outright errors.
 *
 *   4. **Manual** — a fill on an order the operator placed by hand
 *      via the Kalshi/Polymarket UI. Plays an achievement-style
 *      ding ONCE per event. No loop, no burst coalescing — manual
 *      fills are by definition operator-initiated and rare.
 *
 * Priority order (top wins): **alarm > execution > tna > manual**.
 * Rationale: a busy auto-unwind producing fills is the loudest live
 * indicator that everything is working — we DON'T want a TNA dip
 * cue to interrupt the cash-register loop. The operator hears "it
 * is making money" continuously; alarms still punch through as the
 * only "stop and look" signal. TNA defers to execution but takes
 * priority over the manual chime since a TNA flip on a real
 * position is more important than confirming the operator's own
 * click. Manual is the floor — silent during anything else.
 *
 * Anything not in any set stays silent — the row, color, and Slack
 * post are still there, just no sound. Avoids alarm fatigue on
 * lifecycle / digest / recovery-success events.
 */
const EXECUTION_EVENT_TYPES = new Set<NotificationEventType>([
  // Full fills (automation)
  'maker_filled',
  'counter_filled',
  'recovery_filled',
  'crossing_executed',
  // Partial fills (automation)
  'maker_partial_fill',
  'partial_fill_accumulated',
  'crossing_partial',
  'crossing_accumulated',
  // Cycle done
  'execution_complete'
]);

/**
 * Manual-tier events — a fill on an order the operator placed by
 * hand. We only ring on `manual_order_executed`; `manual_order_placed`
 * stays silent (the click already gave the operator confirmation —
 * a sound would be redundant noise).
 */
const MANUAL_EVENT_TYPES = new Set<NotificationEventType>([
  'manual_order_executed'
]);

/**
 * Events that demand operator attention — every one of these is
 * either a leg-risk situation, a platform that just got disabled,
 * or a system halt the operator must clear by hand.
 *
 * `recovery_filled` is special-cased below: it's an execution event
 * when the counter is `executing` (recovery in progress, no
 * intervention needed) but an alarm when `failed` or `no_bid`
 * (operator must hedge manually).
 */
// `emergency_stop` / `entry_emergency_stop` are intentionally NOT in
// this set: panic is operator-initiated (severity `warning`, source
// `manual`) so the doorbell would be triple-redundant — the operator
// just clicked the button. Genuine engine faults (`error`,
// `counter_failed`, `out_of_balance`, `platform_disconnect`) still
// alarm.
const ALARM_EVENT_TYPES = new Set<NotificationEventType>([
  'error',
  'counter_failed',
  'out_of_balance',
  'platform_disconnect'
]);

/** Burst tail for the execution loop. */
const TAIL_MS = 1500;

/** Hard cap on the execution loop per burst. */
const MAX_EXECUTION_PLAY_MS = 5000;

/**
 * Minimum gap between alarm sounds. Several errors firing within
 * this window play ONE alarm — the page row + Slack post still
 * surface every event, but the audio cue isn't repeated. Tuned
 * short enough that a genuine new error 5 s later still alerts.
 */
const ALARM_COOLDOWN_MS = 3000;

/** How long an alarm sound plays before auto-stopping. */
const ALARM_PLAY_MS = 3000;

/**
 * Minimum gap between TNA cues. Sign-flip dedupe at the producer
 * (`TnaAlertService.lastSign`) already prevents repeated firings
 * on a sustained-negative pair, but two different pairs flipping
 * within the cooldown still play one cue — the rows + Slack post
 * still surface every event, the audio is the only deduped surface.
 */
const TNA_COOLDOWN_MS = 3000;

/** How long the TNA cue plays before auto-stopping. */
const TNA_PLAY_MS = 2000;

/**
 * How long the manual-order achievement ding plays before
 * auto-stopping. The asset itself is ~1.5 s; we cap at 2 s so a
 * stale stop-timer can't keep the audio handle busy and block
 * future cues.
 */
const MANUAL_PLAY_MS = 2000;

type Tier = 'execution' | 'manual' | 'tna' | 'alarm';

function classifyEvent(event: NotificationEvent): Tier | null {
  if (ALARM_EVENT_TYPES.has(event.type)) return 'alarm';
  if (event.type === 'recovery_filled') {
    // Recovery "executing" = automation is hedging the leg, no
    // operator action needed → execution tier (cash register).
    // Recovery "failed" / "no_bid" = manual intervention → alarm.
    const counterAction = (event.payload as any)?.counterAction;
    return counterAction === 'executing' ? 'execution' : 'alarm';
  }
  // TNA crossings only fire from the producer when combined contracts
  // ≥ 11 — at this layer we trust that gate and just route to the
  // distinct cue.
  if (event.type === 'tna_negative') return 'tna';
  if (MANUAL_EVENT_TYPES.has(event.type)) return 'manual';
  if (EXECUTION_EVENT_TYPES.has(event.type)) return 'execution';
  return null;
}

/**
 * Plays a cash-register cue for execution events and a doorbell
 * alarm for errors / manual-intervention events. Designed around
 * the operator's reality: a quiet automation produces few sounds,
 * a busy unwind produces ONE rolling cash-register loop, and any
 * error that genuinely needs eyes punches through with a distinct
 * alarm.
 *
 * Pass `null`/`undefined` for ticks where the event has already
 * been consumed (`useNotificationsSocket.acknowledge()`). The hook
 * also guards on `event.id` so an unrelated re-render doesn't
 * replay the cue.
 */
export function useNotificationSound(
  event: NotificationEvent | null | undefined
): void {
  const { load, stop } = useAudioPlayer();
  const lastPlayedId = useRef<string | null>(null);

  // Currently-active tier so an execution loop doesn't clobber an
  // in-progress alarm and a stale event re-render doesn't restart
  // an unrelated sound.
  const currentTier = useRef<Tier | null>(null);

  // Execution-loop bookkeeping (burst-coalesced).
  const executionStartedAt = useRef<number | null>(null);

  // Alarm cooldown — last time an alarm sound started.
  const lastAlarmAt = useRef(0);

  // TNA cooldown — last time a TNA cue started. Independent from
  // the alarm cooldown so a flurry of TNA alerts on different pairs
  // doesn't get throttled by an unrelated alarm cooldown window.
  const lastTnaAt = useRef(0);

  // Single timer ref shared between tiers since only one sound
  // plays at a time. Cleared on every tier transition.
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!event) return;
    if (event.id === lastPlayedId.current) return;
    const tier = classifyEvent(event);
    if (!tier) return;

    lastPlayedId.current = event.id;
    const now = Date.now();

    if (tier === 'alarm') {
      // Alarm cooldown — silent if another alarm just fired.
      if (now - lastAlarmAt.current < ALARM_COOLDOWN_MS) return;
      lastAlarmAt.current = now;

      // Interrupt any execution loop / manual ding in progress —
      // alarms have priority. Operator should hear the doorbell
      // over a running cash register or achievement chime.
      if (stopTimer.current) clearTimeout(stopTimer.current);
      executionStartedAt.current = null;
      currentTier.current = 'alarm';
      try {
        load(alarmSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        currentTier.current = null;
        stopTimer.current = null;
      }, ALARM_PLAY_MS);
      return;
    }

    if (tier === 'tna') {
      // Cooldown so two pairs flipping at once play one cue.
      if (now - lastTnaAt.current < TNA_COOLDOWN_MS) return;
      lastTnaAt.current = now;

      // Defer to alarms AND to the execution cash-register loop —
      // the operator wants to keep hearing the loop while auto-unwind
      // is making money. Interrupts the manual chime since a real
      // position going underwater outranks a click confirmation.
      if (
        currentTier.current === 'alarm' ||
        currentTier.current === 'execution'
      )
        return;
      if (stopTimer.current) clearTimeout(stopTimer.current);
      currentTier.current = 'tna';
      try {
        load(tnaSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        currentTier.current = null;
        stopTimer.current = null;
      }, TNA_PLAY_MS);
      return;
    }

    if (tier === 'manual') {
      // Manual order fill — distinct chime so the operator can hear
      // that the click they made just landed. The lowest-priority
      // tier: stays silent if anything else is playing. The page
      // row + Slack post still surface every event; only the audio
      // is suppressed.
      if (
        currentTier.current === 'alarm' ||
        currentTier.current === 'execution' ||
        currentTier.current === 'tna'
      )
        return;
      if (stopTimer.current) clearTimeout(stopTimer.current);
      currentTier.current = 'manual';
      try {
        load(manualSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        currentTier.current = null;
        stopTimer.current = null;
      }, MANUAL_PLAY_MS);
      return;
    }

    // tier === 'execution' — defer only to alarms. Interrupts TNA
    // and manual cues so the cash-register loop is the dominant
    // audio signal during a busy auto-unwind. Operator wants to
    // hear "money is moving" continuously; alarms still punch
    // through, everything else gives way.
    if (currentTier.current === 'alarm') return;

    if (executionStartedAt.current === null) {
      executionStartedAt.current = now;
      currentTier.current = 'execution';
      try {
        load(cashRegisterSound, { autoplay: true, loop: true });
      } catch {
        executionStartedAt.current = null;
        currentTier.current = null;
        return;
      }
    }

    const cap = executionStartedAt.current + MAX_EXECUTION_PLAY_MS;
    const stopAt = Math.min(now + TAIL_MS, cap);
    const delay = Math.max(0, stopAt - now);

    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = setTimeout(() => {
      stop();
      executionStartedAt.current = null;
      currentTier.current = null;
      stopTimer.current = null;
    }, delay);
  }, [event, load, stop]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (stopTimer.current) clearTimeout(stopTimer.current);
      stop();
    };
  }, [stop]);
}
